import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import SEOHead from "@/components/SEOHead";
import api, { formatPrice } from "@/lib/api";

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const { t, l } = useLanguage();

  const CATEGORIES = [
    { value: "all", label: t("common.all") },
    { value: "peeling", label: t("services.peeling") },
    { value: "steaming", label: t("services.steaming") },
    { value: "massage", label: t("services.massage") },
    { value: "spa_program", label: t("services.spa_program") },
    { value: "package", label: t("services.package") },
  ];

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = activeTab === "all" ? services : services.filter((s) => s.category === activeTab);

  return (
    <div className="py-24 md:py-32" data-testid="services-page">
      <SEOHead path="/services" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.services")}</p>
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight">{t("services.title")}</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-10">
          <TabsList className="bg-card border border-border h-auto flex-wrap gap-1 p-1" data-testid="service-tabs">
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c.value}
                value={c.value}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-none px-4 py-2 text-sm"
                data-testid={`tab-${c.value}`}
              >
                {c.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-card animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="no-services">
            <p className="font-serif text-2xl mb-2">{t("services.notFound")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-stagger">
            {filtered.map((s) => (
              <div
                key={s.id}
                className="p-6 bg-card border border-border hover:border-primary/20 transition-all"
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-serif text-xl">{l(s)}</h3>
                  <span className="text-primary font-semibold font-sans whitespace-nowrap ml-4">{formatPrice(s.price)}</span>
                </div>
                {l(s, "description") && <p className="text-sm text-muted-foreground mb-3">{l(s, "description")}</p>}
                {s.duration && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" /> {s.duration}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
